// מסך תפעול — עבודות: תקועות/נכשלו, אחרונות, ביטול (עורך) ו-retry (מנהל).
import { useEffect, useState } from 'react';
import { api } from '../api';
import { Empty, ErrorBox, Field, JobBadge, Loading, fmtDate, useLoad, useMe } from '../components/ui';
import { PageHead, Tabs, confirmAction, useAction } from '../components/mgmt/common';

interface JobRow { id: string; type: string; status: string; error: string | null; attempts: number; max_attempts: number; progress: any; created_at: string; started_at: string | null; finished_at: string | null; locked_at?: string | null; timeout_ms?: number }
type Tab = 'recent' | 'stuck';

export default function Jobs() {
  const me = useMe();
  const [tab, setTab] = useState<Tab>('recent');
  const [status, setStatus] = useState('');
  const [auto, setAuto] = useState(true);
  const url = tab === 'stuck' ? '/api/jobs?stuck=true&limit=100' : `/api/jobs?limit=100${status ? `&status=${status}` : ''}`;
  const { data, error, loading, reload } = useLoad<JobRow[]>(url);
  const act = useAction();
  const active = (data ?? []).some((j) => j.status === 'queued' || j.status === 'running');
  useEffect(() => { if (!auto || !active) return; const t = setInterval(reload, 3000); return () => clearInterval(t); }, [auto, active, url]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancel = async (j: JobRow) => {
    if (!confirmAction(`לבטל את העבודה ${j.type}? טיוטות קיימות לא יימחקו.`)) return;
    if (await act.run(() => api.post(`/api/jobs/${j.id}/cancel`), 'העבודה בוטלה')) reload();
  };
  const retry = async (j: JobRow) => { if (await act.run(() => api.post(`/api/jobs/${j.id}/retry`), 'העבודה הוחזרה לתור')) reload(); };

  return (
    <div>
      <PageHead title="עבודות" sub="כל פעולה ארוכה (חילוץ, AI, יצוא, בדיקות) רצה כעבודה ברקע. ניסיונות מוגבלים; כשל לא מוחק טיוטה.">
        <button className="btn" onClick={reload} disabled={loading}>{loading ? 'מרענן…' : 'רענון'}</button>
      </PageHead>
      <Tabs<Tab> label="סוג רשימה" value={tab} onChange={setTab} tabs={[{ id: 'recent', label: 'אחרונות' }, { id: 'stuck', label: 'תקועות ונכשלו' }]} />
      <div className="toolbar">
        {tab === 'recent' && (
          <Field label="סטטוס">
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">הכל</option><option value="queued">בתור</option><option value="running">רץ</option><option value="succeeded">הצליח</option><option value="failed">נכשל</option><option value="canceled">בוטל</option>
            </select>
          </Field>
        )}
        <label className="checkline"><input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} /> רענון אוטומטי בזמן ריצה</label>
      </div>
      {me.role !== 'admin' && <div className="small muted" style={{ marginBottom: 8 }}>הפעלה מחדש (retry) מותרת למנהל מערכת בלבד.</div>}
      <ErrorBox error={error} />
      <ErrorBox error={act.error} />
      {loading && !data && <Loading />}
      {data && data.length === 0 && <Empty>{tab === 'stuck' ? 'אין עבודות תקועות או שנכשלו.' : 'אין עבודות.'}</Empty>}
      {data && data.length > 0 && (
        <table className="table cards-sm" aria-live="polite">
          <thead><tr><th>סוג</th><th>סטטוס</th><th>התקדמות</th><th>ניסיונות</th><th>זמנים</th><th>שגיאה</th><th></th></tr></thead>
          <tbody>{data.map((j) => {
            const stuck = j.status === 'running' && j.locked_at && j.timeout_ms && Date.now() - new Date(j.locked_at).getTime() > j.timeout_ms;
            return (
              <tr key={j.id}>
                <td><span className="mono">{j.type}</span><div className="mono small muted">{j.id}</div></td>
                <td data-l="סטטוס"><JobBadge status={j.status} />{stuck && <> <span className="badge err">תקוע</span></>}</td>
                <td data-l="התקדמות" className="small">{fmtProgress(j.progress)}</td>
                <td data-l="ניסיונות" className="ltr">{j.attempts ?? 0}/{j.max_attempts ?? '?'}</td>
                <td data-l="זמנים" className="small muted">נוצר {fmtDate(j.created_at)}{j.started_at && <><br />התחיל {fmtDate(j.started_at)}</>}{j.finished_at && <><br />הסתיים {fmtDate(j.finished_at)}</>}</td>
                <td data-l="שגיאה" className="small" style={{ color: 'var(--danger)', maxWidth: 320, overflowWrap: 'anywhere' }}>{j.error ?? ''}</td>
                <td>
                  <div className="row">
                    {(j.status === 'queued' || j.status === 'running') && me.role !== 'viewer' && <button className="btn small danger" disabled={act.busy} onClick={() => cancel(j)}>ביטול</button>}
                    {(j.status === 'failed' || j.status === 'canceled') && me.role === 'admin' && <button className="btn small" disabled={act.busy} onClick={() => retry(j)}>Retry</button>}
                  </div>
                </td>
              </tr>
            );
          })}</tbody>
        </table>
      )}
    </div>
  );
}

function fmtProgress(p: any): string {
  if (!p) return '—';
  const parts: string[] = [];
  if (p.step) parts.push(String(p.step));
  if (p.message) parts.push(String(p.message));
  if (typeof p.pct === 'number') parts.push(`${Math.round(p.pct)}%`);
  else if (typeof p.percent === 'number') parts.push(`${Math.round(p.percent)}%`);
  if (p.total) parts.push(`${p.done ?? 0}/${p.total}`);
  return parts.join(' · ') || JSON.stringify(p).slice(0, 80);
}
